import { Middleware, createAction } from "@reduxjs/toolkit";
import { Socket, io } from "socket.io-client";

import { SendMessagePayload } from "@/types/message";
import { syncMessage, setReloadMessages } from "@/features/chat/chatSlice";
import { invalidateCacheEntry } from "@/features/chat/conversationCacheSlice";
import { AppDispatch } from "./store";
import { loadConversations, keyExchangeReceived } from "@/features/control/controlSlice";
import { logout } from "@/features/auth/authSlice";
import { fetchMessages } from "@/services/conversationService";
import { GROUP_ADDRESS_TYPE, USERID_ADDRESS_TYPE } from "@/constants/message";
import { processIncomingMessage } from "@/utils/encryptionProcessor";
import {
  initializeEncryption,
  requestPublicKey
} from "@/services/keyExchangeService";
import { getPublicKeySetByUsername } from "@/services/keyStorageService";
import { encryptMessage } from "@/utils/crypto";
import {
  isGroupKeyRequestMessage,
  parseGroupKeyRequestMessage,
  answerGroupKeyRequest,
  sendGroupKeyRequest,
  normalizeGroupAddress,
} from "@/services/groupKeyService";
import { getGroups, getUsers } from "@/services/userGroupService";

let socket: Socket | null = null;

// Dedup concurrent signing-key refresh requests for the same sender so that a burst
// of group messages with a stale key only triggers one @keys fetch.
const pendingSenderKeyRefreshes = new Set<string>();

// Rate-limit group symmetric-key re-requests per group address so a burst of
// undecryptable group messages only sends one group_key_request to the owner.
const lastGroupKeyRefreshAt = new Map<string, number>();
const GROUP_KEY_REFRESH_COOLDOWN_MS = 20_000;

export const connectSocket = createAction<{userId: number; sessionId: string}>("socket/connect");
export const disconnectSocket = createAction("socket/disconnect");
export const sendMessageSocket =
  createAction<SendMessagePayload>("socket/sendMessage");

const websocketMiddleware: Middleware = (store) => (next) => (action) => {
  if (connectSocket.match(action)) {
    const { userId, sessionId } = action.payload;

    // If a socket is already active (connected or still establishing), don't interrupt it.
    // This prevents the "WebSocket closed before connection established" warning when
    // connectSocket is dispatched multiple times (e.g., from both login and checkAuthStatus).
    if (socket?.active) {
      return next(action);
    }

    // Clean up any inactive/dead socket before creating a new one
    if (socket) {
      socket.disconnect();
    }

    socket = io({
      reconnectionAttempts: 10,
      withCredentials: true, // Required for the browser to send session cookies during handshake
      auth: { 
        sessionId,
        userId
      },
    }); // note: url defaults to window.location.origin

    const dispatch: AppDispatch = store.dispatch;

    /**
     * Minimal helper to decrypt messages before dispatching.
     * Preserves the exact original object structure to avoid breaking chatSlice logic.
     */
    const processAndDispatch = async (raw: any) => {
      try {
        const authUserId = store.getState().auth.userId;

        // Extract the message text from either nested or flat structures
        const messageText = raw.formattedMsg?.message || raw.message;

        if (authUserId && messageText) {
          // The WebSocket server JSON.parse()s group addresses into objects.
          // Stringify them back so IDB lookups (keyed by the canonical address string) work.
          const rawAddress = raw.formattedMsg?.address ?? raw.address;
          const peerAddress = typeof rawAddress === 'object' && rawAddress !== null
            ? JSON.stringify(rawAddress)
            : rawAddress;

          // Pre-resolve sender username for group messages that use the 3-part Android wire
          // format (IV:ciphertext:signature) — no username is embedded in the cipher itself.
          // rawAddress.mid is the sender's user ID, set by the WebSocket server from the
          // group routing address JSON.
          let resolvedSenderUsername: string | undefined;
          if (raw.isGroup && typeof rawAddress === 'object' && rawAddress !== null && rawAddress.mid) {
            const senderId = Number(rawAddress.mid);
            if (senderId) {
              try {
                const users = await getUsers();
                resolvedSenderUsername = users.find((u) => u.id === senderId)?.username;
              } catch (err) {
                console.debug('[GroupMsg] Could not resolve sender username:', err);
              }
            }
          }

          const keysVersion = raw.formattedMsg?.keysVersion ?? raw.keysVersion;
          const processed = await processIncomingMessage(messageText, Number(authUserId), peerAddress, resolvedSenderUsername, keysVersion);

          // Clone the object and only swap the message string
          let decryptedPayload = { ...raw };
          if (raw.formattedMsg) {
            decryptedPayload.formattedMsg = {
              ...raw.formattedMsg,
              message: processed.decryptedMessage,
              isEncrypted: processed.isEncrypted,
              isKeyExchange: processed.isKeyExchange,
              ...(processed.senderUsername && { senderUsername: processed.senderUsername }),
            };
          } else if (raw.message) {
            decryptedPayload.message = processed.decryptedMessage;
          }

          // Stale signing key: fetch fresh key for sender then reload the conversation.
          // Covers the Android multi-device case where a new device generates new keys
          // and web still has the old signing key cached from the previous login.
          if (processed.needsKeyRefresh && !pendingSenderKeyRefreshes.has(processed.needsKeyRefresh)) {
            const sender = processed.needsKeyRefresh;
            pendingSenderKeyRefreshes.add(sender);
            requestPublicKey(sender, Number(authUserId))
              .then(() => dispatch(setReloadMessages(true)))
              .catch((err) => console.warn("[E2EE] Signing key refresh failed for:", sender, err))
              .finally(() => pendingSenderKeyRefreshes.delete(sender));
          }

          // Stale/missing group symmetric key (Path B self-heal): an undecryptable group
          // message means our key drifted from the owner's — re-request it; the arriving
          // distribution triggers setReloadMessages (below) to re-decrypt. Rate-limited per group.
          if (processed.needsGroupKeyRefresh) {
            // Normalize to the canonical address (mid="0") — the message carries a per-member
            // variant ({gid,mid:senderId}); getGroups() returns the canonical form.
            const groupAddress = normalizeGroupAddress(processed.needsGroupKeyRefresh);
            const lastAt = lastGroupKeyRefreshAt.get(groupAddress) ?? 0;
            const { username: selfUsername } = store.getState().auth;
            if (Date.now() - lastAt > GROUP_KEY_REFRESH_COOLDOWN_MS && selfUsername && authUserId) {
              // Set the cooldown up front so a burst sends one request; clear it if nothing
              // was actually sent (owner unresolved / we are the owner) or the send failed.
              lastGroupKeyRefreshAt.set(groupAddress, Date.now());
              getGroups()
                .then((groups) => {
                  const group = groups.find((g) => normalizeGroupAddress(g.address) === groupAddress);
                  const ownerName = group?.ownerName;
                  // Only non-owners request; the owner is the source of the key.
                  if (group && ownerName && ownerName !== selfUsername) {
                    return sendGroupKeyRequest(ownerName, group.id, Number(authUserId));
                  }
                  // Couldn't resolve an owner to ask (list not loaded, or we ARE the
                  // owner and shouldn't request) — don't hold the cooldown on a no-op.
                  lastGroupKeyRefreshAt.delete(groupAddress);
                })
                .catch((err) => {
                  console.warn("[GroupKey] Reactive key re-request failed:", err);
                  lastGroupKeyRefreshAt.delete(groupAddress);
                });
            }
          }

          if (processed.isKeyExchange) {
            // keyArrivedFor scopes the per-address retry gates; the global counter always bumps.
            dispatch(keyExchangeReceived(processed.keyArrivedFor));

            // A group key was stored (keyArrivedFor = its canonical address, set only when
            // storage succeeded — covers ECDH-wrapped distributions raw sniffing missed).
            // Open group → reload so 🔒 rows re-decrypt; backgrounded group → invalidate
            // its cache entry below. Not gated on hasUndecrypted: stale-key rows are
            // classified permanent and won't retry on their own.
            if (processed.keyArrivedFor) {
              const keyAddress = processed.keyArrivedFor;
              const state = store.getState();
              const { conversations: convs, currConversationIndex: openIdx } = state.control;
              const openConv = openIdx !== null ? convs[openIdx] : null;
              // Group addresses are {"gid",...} JSON; a plain username means a 1:1 key.
              const isGroupKey = (() => {
                try { return JSON.parse(keyAddress)?.gid !== undefined; } catch { return false; }
              })();
              if (
                openConv &&
                openConv.addressType === GROUP_ADDRESS_TYPE &&
                normalizeGroupAddress(openConv.address) === keyAddress
              ) {
                dispatch(setReloadMessages(true));
              } else if (isGroupKey) {
                // The distributed key's group is NOT on screen. Its cached rows that failed
                // under the old key are classified permanent (groupDecryptFailed), so the
                // per-address version bump alone can't re-arm the cache retry — drop the
                // entry so the next open re-fetches and decrypts with the new key.
                // (1:1 keys are excluded: retryable noPeerKey rows re-arm via the version
                // bump, and invalidating a DM on every TTL key refresh would defeat the cache.)
                const staleEntryKey = Object.keys(state.conversationCache.entries).find(
                  (k) => normalizeGroupAddress(k) === keyAddress
                );
                if (staleEntryKey) dispatch(invalidateCacheEntry(staleEntryKey));
              }
            }

            // If this is a group_key_request and we are the group owner, respond
            // with the encrypted symmetric key. This is the owner side of Path B —
            // members re-request the key when theirs is missing/stale.
            if (isGroupKeyRequestMessage(messageText)) {
              const parsed = parseGroupKeyRequestMessage(messageText);
              if (parsed) {
                const state = store.getState();
                const { userId: ownerId, username: ownerUsername } = state.auth;

                // The group_key_request arrives as a 1:1 message; raw.address is the sender
                const requesterUsername = raw.formattedMsg?.address || raw.address;

                if (ownerUsername && ownerId && requesterUsername) {
                  // Ownership + membership gate and the response live in answerGroupKeyRequest,
                  // shared with the history sweep (owner answering a request that arrived while
                  // it was offline). Fire-and-forget: don't block message delivery on it.
                  answerGroupKeyRequest({
                    requesterUsername,
                    groupId: parsed.group_id,
                    ownerUsername,
                    ownerId: Number(ownerId),
                  }).catch((err) =>
                    console.error("[GroupKey] Failed to respond to key request:", err)
                  );
                }
              }
            }
          }
          dispatch(syncMessage(decryptedPayload));
        } else {
          dispatch(syncMessage(raw));
        }
      } catch (err) {
        console.error("Middleware: Error processing live message", err, raw);
        dispatch(syncMessage(raw)); // Always deliver the raw message rather than silently dropping it
      }
    };

    // Automated Key Exchange on Connection
    const setupEncryption = async () => {
      try {
        if (userId === undefined || userId === null || Number.isNaN(Number(userId))) {
          console.warn("E2E Encryption: Cannot initialize without a valid userId");
          return;
        }

        // Ensure the keypair exists on connect (idempotent — initializeEncryption returns
        // the cached keyId if already initialized). Publishing the public key to @keys is
        // handled by useEncryption.automateKeyExchange, so the middleware no longer
        // double-publishes it here.
        await initializeEncryption(userId);
      } catch (err) {
        console.error("E2E Encryption: Automation failed", err);
      }
    };
    
    // Move encryption setup to 'connect' event to ensure Redux state
    // (routerId, username) is populated before background HTTP requests are made.
    // Also dispatch keyExchangeReceived() on connect so that ChatInputs' checkEncryption
    // effect re-runs with the socket live.  On first login the socket often finishes
    // connecting AFTER checkEncryption has already called requestPublicKey, causing the
    // @keys WebSocket response to be dropped (client not yet in the room).  The dispatch
    // resets lastKeyFetchAddressRef and triggers a fresh requestPublicKey while the
    // socket is confirmed connected, so the response is guaranteed to arrive.
    socket.on("connect", () => {
      console.log("WebSocket connected, setting up encryption automation...");
      dispatch(keyExchangeReceived());
      setupEncryption();
    });
    
    socket.off("receiveMessage").on("receiveMessage", (message) => {
      processAndDispatch(message);
    });

    socket.off("syncSendMessage").on("syncSendMessage", (message) => {
      processAndDispatch(message);
    });

    socket.on("disconnect", (reason, details) => {
      if (!socket?.active) {
        console.error(
          "websocket disconnected:",
          reason,
          new Date().toISOString()
        ); // the reason of the disconnection, for example "transport error"
        console.error(details); // the low-level reason of the disconnection, for example "xhr post error"
      }
    });

    socket.on("reconnect", async (n) => {
      console.log("Successfully reconnected after", n, "attempt(s)");
      const state = store.getState();
      const { conversations, currConversationIndex } = state.control;
      const { maxRmsgId, maxSmsgId } = state.chat;

      // update curr conversation messages + list of conversations
      if (currConversationIndex !== null) {
        const { address, addressId, addressType } = conversations[currConversationIndex];
        const newMessages = await fetchMessages(
          address,
          addressId,
          addressType,
          Number(state.auth.userId) || undefined,
          maxSmsgId,
          maxRmsgId
        );
        newMessages.messages.forEach(formattedMsg => {
          dispatch(syncMessage({ address, isGroup: addressType === GROUP_ADDRESS_TYPE, formattedMsg }));
        });
        dispatch(loadConversations());
      }

    });

    socket.on("reconnect_error", (err) => {
      console.error(err);
    });

    socket.on("reconnect_failed", () => {
      console.error("reconnect failed");
      dispatch(logout());
    });

    socket.on("connect_error", (err) => {
      console.error(err);
      if (err.message === "Unauthorized" || err.message.includes("jwt")) {
        dispatch(logout());
      }
    });

    socket.on("error", (err) => {
      console.error(err);
    });
  }

  if (disconnectSocket.match(action)) {
    socket?.disconnect();
    socket = null;
  }

  if (sendMessageSocket.match(action)) {
    const handleSend = async () => {
      const payload = { ...action.payload };
      const state = store.getState();
      const currentUserId = state.auth.userId;

      // Automated Encryption: Only for direct user-to-user messages
      if (payload.address_type === USERID_ADDRESS_TYPE && currentUserId && payload.address) {
        try {
          let peerKeys = await getPublicKeySetByUsername(payload.address);

          // On-demand fetch if key is missing
          if (!peerKeys) {
            await requestPublicKey(payload.address, Number(currentUserId));
            peerKeys = await getPublicKeySetByUsername(payload.address);
          }

          if (peerKeys) {
            const { getActiveKeySet } = await import("@/services/keyStorageService");
            const ownKeySet = await getActiveKeySet(Number(currentUserId));
            if (ownKeySet) {
              payload.message = await encryptMessage(
                payload.message,
                peerKeys.encryptPublicKeyJwk,
                ownKeySet.encryptPrivateKeyJwk,
                ownKeySet.signPrivateKeyJwk
              );
            }
          }
        } catch (error) {
          console.error("WebsocketMiddleware: Encryption failed, sending as plaintext", error);
        }
      }
      socket?.emit("sendMessage", payload);
    };
    handleSend();
  }

  return next(action);
};

export default websocketMiddleware;
